// src/components/RecommendationModal.jsx
import React, { useState } from 'react';
import { amortizedTotal } from '../calculators/chapter13Calculator';
import {
  CheckCircleIcon,
  AcademicCapIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import attorneysData from '../json/verified_attorneys_contact_info.json'
const API_URL = process.env.REACT_APP_API_URL;
export default function Chapter13PlanModal({
  isOpen,
  onClose,
  recommendationResult,
  answers,
  onAnswersChange,
}) {
  // Pick 3–5 random attorneys for the user’s district
const [recommendedList] = useState(() => {
  const district = answers.consultationResult.computedValues.plan36.district;
  const allList = attorneysData[district] || [];
  const validList = allList.filter(att => att.address_valid);
  const sourceList = validList.length >= 5 ? validList : allList;
  return sourceList.sort(() => Math.random() - 0.5).slice(0, 5);
});

const [selectedAttorney, setSelectedAttorney] = useState(null);
const [showAttorneyDetails, setShowAttorneyDetails] = useState(false);
const [showContactForm, setShowContactForm] = useState(false);
const [contactInfo, setContactInfo] = useState({
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phone: ''
});
  const [isSubmittingAttorney, setIsSubmittingAttorney] = useState(false);
  const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState(false);
  const [lowCostCh13Selected, setLowCostCh13Selected] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [selectedCh13Plan, setSelectedCh13Plan] = useState(36);
  const { recommendation, fees = {}, computedValues = {} } = recommendationResult;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSwitchingChapter, setIsSwitchingChapter] = useState(false);
  // Build a caseId string matching the backend’s generate_case_id
  function buildCaseId(data) {
    const slugify = s => s.toLowerCase().replace(/\s+/g, '-');
    let parts = [ slugify(data.lastName), slugify(data.firstName) ];
    if (data.middleName) parts.push(slugify(data.middleName));
    let namePart = parts.join('-');
  
    if (data.spouseFiling?.toLowerCase() === 'yes') {
      const sp = [
        slugify(data.spouseLastName),
        slugify(data.spouseFirstName),
        slugify(data.spouseMiddleName),

      ].filter(Boolean).join('-');
      namePart = `${namePart}&${sp}`;
    }
  
    return `${namePart}-${data.dob}`;  // dob already in YYYY-MM-DD
  }
  const caseId = buildCaseId(answers);
  const switchChapter = async (chapter) => {
    const url = `${process.env.REACT_APP_API_URL}/case/${caseId}/switch-chapter-intake/`;
    const resp = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ chapter }),
    });
    if (!resp.ok) {
      const { error } = await resp.json().catch(() => ({}));
      throw new Error(error || resp.statusText);
    }
    return resp.json();
  };
  const acceptDisclaimer = async () => {
    try {
        setIsSwitchingChapter(true);
      await switchChapter('13');
      setIsSwitchingChapter(false)
      setLowCostCh13Selected(true);
      setShowDisclaimerModal(false);
          // or refresh UI however you like
    } catch (e) {
        setIsSwitchingChapter(false);
      alert('Could not switch to Chapter 13:\n' + e.message);
    }
  };
  // when they toggle the checkbox
  const handleLowCostToggle = async (checked) => {
    if (checked) {
      // show disclaimer before actually switching
      setShowDisclaimerModal(true);
    } else {
      try {
        setIsSwitchingChapter(true);
        await switchChapter('7');
        setLowCostCh13Selected(false);
      } catch (e) {
        alert('Could not switch back to Chapter 7:\n' + e.message);
      } finally {
        setIsSwitchingChapter(false);
      }
    }
  };
  // sum medical debts by parsing amountOwed
  // --- Contact validation state and helpers ---
  const [contactErrors, setContactErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  function validateName(name) {
    return name.trim().length > 0 ? '' : 'Name is required';
  }

  function validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email) ? '' : 'Invalid email address';
  }

  function validatePhone(phone) {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 ? '' : 'Phone number must be 10 digits';
  }
  function formatPhoneNumber(value) {
    const digits = ('' + value).replace(/\D/g, '');
    const len = digits.length;
    if (len < 4) {
      // Just 1–3 digits
      return digits;
    } else if (len < 7) {
      // 4–6 digits: (123) 4 or (123) 45 or (123) 456
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      // 7+ digits: (123) 456-7 or up to 10 digits
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  }
const handleAttorneyRequestSubmit = async () => {
    // block if any validation errors remain
    const hasErrors = Object.values(contactErrors).some(e => e);
    if (hasErrors) return;

    setIsSubmittingAttorney(true);

    // 1) Save progress
    try {
      await fetch(`${API_URL}/chapter-13-step/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          last_step_reached: 'attorney_request'
        })
      });
    } catch (e) {
      console.error('Error saving progress:', e);
    }

    // 2) Notify attorney backend
    try {
      const payload = {
        answers,
        recommendedList
      };
      await fetch(`${API_URL}/send-attorney-alert/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      console.error('Error sending attorney alert:', e);
    }

    // 3) Show confirmation
    setIsSubmittingAttorney(false);
    setShowSubmissionConfirmation(true);
    setShowContactForm(false)
  };
  const primaryMedicalDebt = Math.round((answers.medicalDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));
  const spouseMedicalDebt = Math.round((answers.spouseMedicalDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));
  const medicalDebt = primaryMedicalDebt + spouseMedicalDebt
  // helper to compute total payments over 3 years at 25% APR
  function totalAmortized(amount) {
    const r = 0.25 / 12;
    const n = 36;
    const m = amount * (r / (1 - (1 + r) ** -n));
    return m * n;
  }

  // sum and amortize personal loans
  const personalLoanTotal = (answers.personalLoanDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const primaryPersonalLoanPayment = Math.round(totalAmortized(personalLoanTotal));

  const spousePersonalLoanTotal = (answers.spousePersonalLoanDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const spousePersonalLoanPayment = Math.round(totalAmortized(spousePersonalLoanTotal));

const personalLoanPayment = primaryPersonalLoanPayment + spousePersonalLoanPayment
  // sum and amortize credit card debts
  const creditCardTotal = (answers.creditCardDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const primaryCreditCardPayment = Math.round(totalAmortized(creditCardTotal));

  const spouseCreditCardTotal = (answers.spouseCreditCardDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.debtBalance || 0),
    0
  );
  const spouseCreditCardPayment = Math.round(totalAmortized(spouseCreditCardTotal));

const creditCardPayment = primaryCreditCardPayment + spouseCreditCardPayment
  // sum other debts by parsing amountOwed
  const primaryOtherDebt = Math.round((answers.otherDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));

  const spouseOtherDebt = Math.round((answers.spouseOtherDebtDetails || []).reduce(
    (sum, d) => sum + parseFloat(d.amountOwed || 0),
    0
  ));

  const otherDebt = primaryOtherDebt + spouseOtherDebt

  // total cost not to file
  const costNotToFile =
    medicalDebt +
    personalLoanPayment +
    creditCardPayment +
    otherDebt

    const totalSavings = costNotToFile - (fees.total || 0);
  if (!isOpen) return null;

  // Build slide decks for each case
  const slidesByRec = {
    "Chapter13":[
      {
        icon: AcademicCapIcon,
        title: 'What is Chapter 13 Plan?',
        content: (
          <div className="space-y-4 text-gray-700">
            <p>
              Your Chapter 13 plan is a court-approved repayment of your debts with some benefits and some drawbacks.
            </p>
            <h4 className="font-semibold">Pros</h4>
            <ul className="list-disc list-inside ml-5 space-y-1">
              <li>Amounts owed freeze at filing and additional interest and finance charges no longer accrue</li>
              <li>If qualified, you can "cramdown" payments on car loans to get a better interest rate or monthly payment</li>
              <li>You may only have to pay a very small portion of your unsecured debts (as low as 1%)</li>
            </ul>
            <h4 className="font-semibold">Cons</h4>
            <ul className="list-disc list-inside ml-5 space-y-1">
              <li>While in the plan, NO NEW DEBT unless approved by the court</li>
              <li>Your tax refunds are subject to the plan and you can only keep up to ${computedValues.plan36.refundAllowed}</li>
              <li>If you choose a 36 month plan, ALL tax refunds must be paid into the plan</li>
            </ul>
          </div>
        ),
      },
      {
        icon: CurrencyDollarIcon,
        title: 'Your Chapter 13 Plan',
        content: (
          <div className="space-y-6 text-gray-700 px-4 sm:px-6">
            {/* Plan summary header */}
            {(() => {
              const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
              // Destructure monthlyPayment, mortgageItems, vehicleItems

              const { monthlyPayment, mortgageItems, vehicleItems } = planData;
              // Compute excluded secured total and names
              const excludedTotal =
                mortgageItems
                  .filter(i => !i.included)
                  .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0)
                +
                vehicleItems
                  .filter(i => !i.included)
                  .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0);
              const excludedNames = [
                ...mortgageItems.filter(i => !i.included).map(i => i.creditor + ' Mortgage'),
                ...vehicleItems.filter(i => !i.included).map(i => i.description + ' Loan'),
              ];
              return (
                <div className="px-4 sm:px-6">
                  <p className="text-gray-700">
                    Here’s your Chapter 13 plan payment and breakdown:
                  </p>
                  <p className="text-3xl sm:text-4xl font-bold text-indigo-600 mt-2 mb-4">
                    ${monthlyPayment.toLocaleString()}<span className="text-base font-medium text-gray-600"> / month</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    + ${Math.round(excludedTotal).toLocaleString()} paid directly to secured creditors outside of the plan:
                    {excludedNames.length > 0 ? (
                      <span className="font-medium"> {excludedNames.join(', ')}</span>
                    ) : (
                      <span className="italic"> None</span>
                    )}
                  </p>
                </div>
              );
            })()}

            {/* Plan toggle */}
            <div className="flex space-x-4">
              <button
                className={`px-3 py-1 rounded ${selectedCh13Plan === 36 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedCh13Plan(36)}
              >
                36 months
              </button>
              <button
                className={`px-3 py-1 rounded ${selectedCh13Plan === 60 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}
                onClick={() => setSelectedCh13Plan(60)}
              >
                60 months
              </button>
            </div>

            {/* Breakdown header */}
            <div className="mt-4 mb-2">
              <h4 className="text-lg font-semibold text-gray-800">
                Monthly Breakdown
              </h4>
            </div>

            {/* Simplified accordion only for secured line items */}
            <div className="space-y-2 text-sm sm:text-base px-0 sm:px-6">
              {(() => {
                const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
                const {
                  mortgageItems,
                  vehicleItems,
                  priorityItems,
                  unsecuredItems,
                  attorneyBalance,
                  trusteeFee,
                  monthlyPayment
                } = planData;
                const courtFee = priorityItems.filingFee;
                const {
                  unsecuredTotal,
                  unsecuredPlan,
                  totalLiquidationValue,
                  disposableIncomeResults,
                } = planData;
                const percent = unsecuredTotal > 0
                  ? (unsecuredPlan / unsecuredTotal)
                  : 0;
                const monthlyArrearage = i => i.totalArrearage / selectedCh13Plan;

                return (
                  <>
                    {/* Mortgage section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Mortgage Payments: $
                        {Math.round(
                          mortgageItems
                            .filter(i => i.included)
                            .reduce((sum, i) => sum + Number(i.monthlyPayment) + monthlyArrearage(i), 0)
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {mortgageItems
                          .filter(i => i.included)
                          .map((i, idx) => {
                            const amt = Math.round(Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan));
                            return (
                              <div
                                key={idx}
                                className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                              >
                                <span>{i.creditor}</span>
                                <span>${amt.toLocaleString()}</span>
                              </div>
                            );
                          })}
                      </div>
                    </details>

                    {/* Vehicle section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Vehicle Payments: $
                        {Math.round(
                          vehicleItems
                            .filter(i => i.included)
                            .reduce((sum, i) => sum + Number(i.total/selectedCh13Plan), 0)
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {vehicleItems.filter(i => i.included).map((i, idx) => {
                          const amt = Math.round(Number(i.total / selectedCh13Plan));
                          return (
                            <div
                              key={idx}
                              className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                            >
                              <span>{i.description}</span>
                              <span>${amt.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </details>

                    {/* Priority Debts section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Priority Debts: $
                        {Math.round(
                          (priorityItems.respondentTaxLast3 +
                            priorityItems.spouseTaxLast3 +
                            priorityItems.respondendentChildSupportArrears +
                            priorityItems.spouseChildSupportArrears) /
                            selectedCh13Plan
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {[
                          ['Recent Tax Debt', priorityItems.respondentTaxLast3 + priorityItems.spouseTaxLast3],
                          ['Child Support', priorityItems.respondendentChildSupportArrears + priorityItems.spouseChildSupportArrears],
                          ['Alimony', priorityItems.alimony || 0],
                        ].filter(([, total]) => total > 0).map(([label, total], idx) => (
                          <div
                            key={label}
                            className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                          >
                            <span>{label}</span>
                            <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Unsecured Debts section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Unsecured Debts: $
                        {Math.round(
                          ((unsecuredItems.creditCardTotal +
                            unsecuredItems.spouseCreditCardTotal +
                            unsecuredItems.personalLoanTotal +
                            unsecuredItems.spousePersonalLoanTotal +
                            unsecuredItems.medicalDebtTotal +
                            unsecuredItems.spouseMedicalDebtTotal +
                            unsecuredItems.nonPriorityTaxTotal +
                            unsecuredItems.spouseNonPriorityTaxTotal +
                            unsecuredItems.otherDebtTotal +
                            unsecuredItems.spouseOtherDebtTotal) /
                            selectedCh13Plan) * percent
                        ).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {[
                          ['Credit Cards', unsecuredItems.creditCardTotal* percent + unsecuredItems.spouseCreditCardTotal * percent],
                          ['Personal Loans', unsecuredItems.personalLoanTotal* percent + unsecuredItems.spousePersonalLoanTotal* percent],
                          ['Medical', unsecuredItems.medicalDebtTotal* percent + unsecuredItems.spouseMedicalDebtTotal* percent],
                          ['NonPriority Tax', unsecuredItems.nonPriorityTaxTotal* percent + unsecuredItems.spouseNonPriorityTaxTotal* percent],
                          ['Other', unsecuredItems.otherDebtTotal* percent + unsecuredItems.spouseOtherDebtTotal* percent],
                        ].map(([label, total], idx) => (
                          <div
                            key={label}
                            className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                          >
                            <span>{label}</span>
                            <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Admin Costs section */}
                    <details className="border rounded">
                      <summary className="px-4 py-2 font-semibold text-sm sm:text-base">
                        Admin Costs: $
                        {Math.round((attorneyBalance + courtFee + trusteeFee) / selectedCh13Plan).toLocaleString()}
                      </summary>
                      <div className="divide-y divide-gray-200">
                        {[
                          ['Attorney Fees', attorneyBalance],
                          ['Court Filing Fee', courtFee],
                          ['Trustee Fee', trusteeFee],
                        ].map(([label, total], idx) => (
                          <div
                            key={label}
                            className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                          >
                            <span>{label}</span>
                            <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </>
                );
              })()}
            </div>
          </div>
        ),
      },
      {
        icon: RocketLaunchIcon,
        title: 'Ready to Start?',
        content: (
          <div className="space-y-4 text-gray-700 px-4 sm:px-6">
            <p>Would you like one of these recommended Ch 13 attorneys in your district to reach out to you?</p>
            <div className="space-y-2 mt-2">
              {recommendedList.map((att, idx) => (
                <div
                  key={idx}
                  className={`cursor-pointer flex justify-between items-center px-4 py-2 rounded ${idx % 2 === 0 ? 'bg-indigo-50' : 'bg-indigo-100'} hover:bg-indigo-200`}
                  onClick={() => {
                    setSelectedAttorney(att);
                    setShowAttorneyDetails(true);
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <InformationCircleIcon className="h-5 w-5 text-indigo-500" />
                    <span className="font-medium text-gray-800">{att.name}</span>
                  </div>
                  <span className="text-sm text-gray-600">{att.city}, {att.state}</span>
                </div>
              ))}
            </div>

            {/* Attorney Details Modal */}
            {showAttorneyDetails && selectedAttorney && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg max-w-sm w-full">
                  <h4 className="text-lg font-semibold mb-2">{selectedAttorney.name}</h4>
                  <p>
                    {selectedAttorney.street}
                    {selectedAttorney.street2 ? `, ${selectedAttorney.street2}` : ''}
                  </p>
                  <p>
                    {selectedAttorney.city}, {selectedAttorney.state} {selectedAttorney.zip}
                  </p>
                  <p className="mt-2">Phone: {selectedAttorney.phone}</p>
                  <p>Email: {selectedAttorney.email}</p>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      className="px-3 py-1 bg-gray-200 rounded"
                      onClick={() => setShowAttorneyDetails(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
            <button
              className="px-3 py-1 bg-indigo-600 text-white rounded"
              onClick={() => {
                setShowAttorneyDetails(false);
                setShowContactForm(true);
              }}
            >
              Yes, contact me
            </button>
            {/* Contact Form Modal */}
            {showContactForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg max-w-sm w-full">
                  <h4 className="text-lg font-semibold mb-2">Your Contact Information</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      className="w-full border rounded px-3 py-2"
                      value={answers.firstName || ''}
                      onChange={e => onAnswersChange({ ...answers, firstName: e.target.value })}
                    />
                    <input
                      type="text"
                      name="middleName"
                      placeholder="Middle Name"
                      className="w-full border rounded px-3 py-2"
                      value={answers.middleName || ''}
                      onChange={e => onAnswersChange({ ...answers, middleName: e.target.value })}
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      className="w-full border rounded px-3 py-2"
                      value={answers.lastName || ''}
                      onChange={e => onAnswersChange({ ...answers, lastName: e.target.value })}
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      className="w-full border rounded px-3 py-2"
                      value={answers.email || ''}
                      onChange={e => onAnswersChange({ ...answers, email: e.target.value })}
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Your Phone"
                      className="w-full border rounded px-3 py-2"
                      value={answers.phone || ''}
                      onChange={e => onAnswersChange({ ...answers, phone: e.target.value })}
                    />
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      className="px-3 py-1 bg-gray-200 rounded"
                      onClick={() => setShowContactForm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-1 bg-indigo-600 text-white rounded"
                      onClick={handleAttorneyRequestSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ),
      },
      // --- NEW SLIDE: Secured Debts ---
      {
        icon: DocumentDuplicateIcon,
        title: 'Secured Debts',
        content: (() => {
          // pull in the plan data for the current selection
          const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
          const includedMortgages = planData.mortgageItems.filter(i => i.included);
          const excludedMortgages = planData.mortgageItems.filter(i => !i.included);
          const includedVehicles = planData.vehicleItems.filter(i => i.included);
          const excludedVehicles = planData.vehicleItems.filter(i => !i.included);

          return (
            <div className="space-y-4 text-gray-700 px-4 sm:px-6">
              <p>
                Certain secured debts—your mortgages and vehicle loans—are included in your Chapter 13 plan.
                You will no longer need to make separate payments on these included debts; they are paid through
                your plan payment.
              </p>

              {/* Included Debts */}
              <div>
                <h4 className="font-semibold mb-2">Secured Debts Included in Plan</h4>
                {includedMortgages.length + includedVehicles.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {includedMortgages.map((m, idx) => (
                      <div
                        key={`inc-mort-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{m.creditor} Mortgage</span>
                        <span>Included</span>
                      </div>
                    ))}
                    {includedVehicles.map((v, idx) => (
                      <div
                        key={`inc-veh-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{v.description} Loan</span>
                        <span>Included</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="italic">None</p>
                )}
              </div>

              {/* Excluded Debts */}
              <div>
                <h4 className="font-semibold mb-2">Secured Debts Not Included in Plan*</h4>
                {excludedMortgages.length + excludedVehicles.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {excludedMortgages.map((m, idx) => (
                      <div
                        key={`exc-mort-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{m.creditor} Mortgage</span>
                        <span>Continue Direct Payment</span>
                      </div>
                    ))}
                    {excludedVehicles.map((v, idx) => (
                      <div
                        key={`exc-veh-${idx}`}
                        className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                      >
                        <span>{v.description} Vehicle Loan</span>
                        <span>Continue Direct Payment</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="italic">None</p>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 italic">
                * We leave certain debts out of the plan to avoid trustee fees on each dollar paid into the plan.
              </p>
            </div>
          );
        })(),
      },
      // --- NEW SLIDE: Priority Debts ---
      {
        icon: CurrencyDollarIcon,
        title: 'Priority Debts',
        content: (() => {
          const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
          const { priorityItems } = planData;
          const debts = [
            ['Recent Tax Debt', priorityItems.respondentTaxLast3 + priorityItems.spouseTaxLast3],
            ['Child Support Arrears', priorityItems.respondendentChildSupportArrears + priorityItems.spouseChildSupportArrears],
          ].filter(([, total]) => total > 0);
          return (
            <div className="space-y-4 text-gray-700 px-4 sm:px-6">
              <p>
                Priority debts must be paid in full through your Chapter 13 plan. Below are your priority monthly obligations:
              </p>
              {debts.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {debts.map(([label, total], idx) => (
                    <div
                      key={idx}
                      className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50"
                    >
                      <span>{label}</span>
                      <span>${Math.round(total / selectedCh13Plan).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="italic">None</p>
              )}
            </div>
          );
        })(),
      },
      // --- NEW SLIDE: Unsecured Debts Analysis ---
      {
        icon: DocumentDuplicateIcon,
        title: 'Unsecured Debts Analysis',
        content: (() => {
          const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
          const {
            unsecuredItems,
            unsecuredTotal,
            unsecuredPlan,
            totalLiquidationValue,
            disposableIncomeResults,
          } = planData;
          const percent = unsecuredTotal > 0
            ? Math.round((unsecuredPlan / unsecuredTotal) * 100)
            : 0;
          const dispInc = recommendationResult.computedValues.plan36.disposableIncomeResults.disposableIncome;
          return (
            <div className="space-y-4 text-gray-700 px-4 sm:px-6">
              <p>
                Your Chapter 13 plan pays back <strong>{percent}%</strong> of your unsecured debts (${unsecuredPlan.toLocaleString()} total) over {selectedCh13Plan} months.
              </p>
              <p>
                This percentage is set by two rules: the <strong>disposable income test</strong> determines how much you can afford based on your income and your average cost of living, and the <strong>best interest test</strong> determines what your creditors would receive if you liquidated all your assets.
              </p>
              <h4 className="font-semibold mb-2">Applicable Test(s):</h4>
              {[
                totalLiquidationValue > 0 && {
                  label: 'Best interest test',
                  message: `You must pay at least $${totalLiquidationValue.toLocaleString()} total to unsecured creditors up to 100%.`,
                },
                dispInc > 0 && {
                  label: 'Disposable income test',
                  message: `You must pay at least $${dispInc.toLocaleString()} per month up to 100%.`,
                }
              ]
                .filter(Boolean)
                .map((test, idx) => (
                  <p key={idx}>
                    <strong>{test.label}:</strong> {test.message}
                  </p>
                ))}
              {totalLiquidationValue <= 0 && dispInc <= 0 && (
                <p>
                  No minimum unsecured payment is required under either test.
                </p>
              )}
            </div>
          );
        })(),
      },
      {
        icon: DocumentDuplicateIcon,
        title: 'How to Pay the Plan',
        content: (
          <div className="space-y-4 text-gray-700 px-4 sm:px-6">
            <p>
              Your Chapter 13 plan payment is made as a single monthly payment to the Chapter 13 trustee. The trustee’s job is to distribute those funds to your creditors in accordance with the law and your confirmed plan.
            </p>
            <p>If you are employed, the preferred method is via a wage order sent to your employer. Some jurisdictions also allow:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-sm sm:text-base">
              <li>ACH (electronic) payments</li>
              <li>Personal checks</li>
              <li>Cashier’s checks</li>
            </ul>
            <p className="mt-2 text-xs text-gray-500 italic">
              Your first plan payment is due within 30 days after your case is filed.
            </p>
          </div>
        ),
      },

            // --- NEW SLIDE: Monthly Savings ---
            {
                icon: ArrowTrendingUpIcon,
                title: 'Monthly Savings',
                content: (() => {
                  const planData = recommendationResult.computedValues[`plan${selectedCh13Plan}`];
                  const {
                    monthlyPayment,
                    mortgageItems,
                    vehicleItems,
                    unsecuredItems,
                    priorityItems
                  } = planData;
        
                  // Secured debts paid outside plan
                  const excludedSecuredMonthly =
                    mortgageItems.filter(i => !i.included)
                      .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0) +
                    vehicleItems.filter(i => !i.included)
                      .reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0);
        
                  // NEW: total secured monthly (all secured debts, included or excluded)
                  const totalSecuredMonthly =
                    mortgageItems.reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0) +
                    vehicleItems.reduce((sum, i) => sum + Number(i.monthlyPayment) + (i.totalArrearage / selectedCh13Plan), 0);
        
                  const planMonthlyCost = Math.round(monthlyPayment + excludedSecuredMonthly);
        
                  // Unsecured amortized portions
                  const creditCardMonthly = Math.round(
                    amortizedTotal(
                      unsecuredItems.creditCardTotal + unsecuredItems.spouseCreditCardTotal,
                      0.25,
                      selectedCh13Plan
                    ) / selectedCh13Plan
                  );
                  const personalLoanMonthly = Math.round(
                    amortizedTotal(
                      unsecuredItems.personalLoanTotal + unsecuredItems.spousePersonalLoanTotal,
                      0.25,
                      selectedCh13Plan
                    ) / selectedCh13Plan
                  );
                  const medicalMonthly = Math.round(
                    (unsecuredItems.medicalDebtTotal + unsecuredItems.spouseMedicalDebtTotal) /
                      selectedCh13Plan
                  );
                  const otherMonthly = Math.round(
                    (unsecuredItems.otherDebtTotal + unsecuredItems.spouseOtherDebtTotal) /
                      selectedCh13Plan
                  );
                  // Tax obligations amortized at 10%
                  const taxObligationTotal = priorityItems.respondentTaxLast3 + priorityItems.spouseTaxLast3;
                  const taxMonthly = Math.round(
                    amortizedTotal(taxObligationTotal, 0.10, selectedCh13Plan) / selectedCh13Plan
                  );
                  // Child support & alimony over plan
                  const supportTotal = 
                    priorityItems.respondendentChildSupportArrears +
                    priorityItems.spouseChildSupportArrears +
                    (priorityItems.alimony || 0);
                  const supportMonthly = Math.round(supportTotal / selectedCh13Plan);
        
                  // Use totalSecuredMonthly for current monthly cost
                  const currentMonthlyCost = Math.round(
                    totalSecuredMonthly +
                    creditCardMonthly +
                    personalLoanMonthly +
                    medicalMonthly +
                    otherMonthly +
                    taxMonthly +
                    supportMonthly
                  );
                  const savings = currentMonthlyCost - planMonthlyCost;
        
                  return (
                    <div className="space-y-4 text-gray-700 px-4 sm:px-6">
                      <p>Your estimated* current monthly debt payments:</p>
                      <div className="divide-y divide-gray-200">
                        {totalSecuredMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Total Secured Debts</span>
                            <span>${totalSecuredMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {creditCardMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                            <span>Credit Cards</span>
                            <span>${creditCardMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {personalLoanMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Personal Loans</span>
                            <span>${personalLoanMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {medicalMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                            <span>Medical Bills</span>
                            <span>${medicalMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {taxMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base even:bg-gray-50">
                            <span>Tax Obligations</span>
                            <span>${taxMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {otherMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Other Debts</span>
                            <span>${otherMonthly.toLocaleString()}</span>
                          </div>
                        )}
                        {supportMonthly > 0 && (
                          <div className="flex justify-between p-2 text-sm sm:text-base">
                            <span>Child Support & Alimony Arrears</span>
                            <span>${supportMonthly.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 italic">
                        * Estimates based on payments spread over {selectedCh13Plan} months.
                      </p>
                      <p className="font-semibold mt-4">
                        Current Debt Payments: ${currentMonthlyCost.toLocaleString()}
                      </p>
                      <p className="font-semibold">
                        Chapter 13 Debt Payments: ${planMonthlyCost.toLocaleString()}
                      </p>
                      <p className="font-semibold text-green-600">
                        Estimated Monthly Savings: ${savings.toLocaleString()}
                      </p>
                      <p className="font-bold mt-2 text-green-600 underline">
                        Total Savings over {selectedCh13Plan} months: ${(savings * selectedCh13Plan).toLocaleString()}
                      </p>
                    </div>
                  );
                })(),
              },
    ]

  };

  // pick the right deck
  const slides = slidesByRec[recommendation] || slidesByRec['Chapter13'];
  const SlideIcon = slides[currentSlide].icon;
  const isLast = currentSlide === slides.length - 1;
  const isFirst = currentSlide === 0;

  return (
    
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Card container */}
      {showDisclaimerModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
      <h2 className="text-lg font-semibold mb-4">Disclaimer: Low-Cost Chapter 13 Plan</h2>
      <p className="mb-4 text-gray-700">
        Chapter 13 gives you the same creditor protections but requires you to commit to payments of
        $145 per month for 36 months, plus a $100 upfront fee. Additionally:
      </p>
      <li>Your payments will be deducted from your wages by court order to your employer</li>
      <li>You must give all tax refunds to the Plan</li>
      <li>You cannot acquire debt while in the Plan</li>
      <br />
      <div className="flex justify-end space-x-4">
        <button
          onClick={acceptDisclaimer}
          disabled={isSwitchingChapter}
          className={`bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 flex items-center justify-center ${isSwitchingChapter ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSwitchingChapter && (
            <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
          )}
          Accept
        </button>
        <button
          onClick={() => {
            setLowCostCh13Selected(false);
            setShowDisclaimerModal(false);
          }}
          className="text-gray-600 hover:underline"
        >
          No Thanks
        </button>
      </div>
    </div>
  </div>
)}

      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto my-4 overflow-auto max-h-[90vh] p-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-4">
          
          <div className="w-full flex items-center space-x-2 -mx-3 -mt-3 -my-3
            bg-gradient-to-r from-indigo-200 to-blue-20 bg-opacity-75
            text-gray-800
            rounded-2xl
            text-xl font-semibold
            px-6 py-3
            whitespace-nowrap">
            
            { SlideIcon && <SlideIcon className="w-6 h-6 text-indigo-600" /> }
            <h3 className="">
              {slides[currentSlide].title}
            </h3>
          </div>
          
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="mb-6">
          
          {slides[currentSlide].title === 'Cost Not to File' ? (
            <div className="space-y-6 text-gray-700">
              {/* 1. Grouped debt breakdown */}
              <div className="text-lg font-medium">Debt Cost Breakdown</div>
              <ul className="list-disc list-inside space-y-1">
                {medicalDebt > 0 && <li>Medical debt: ${medicalDebt.toLocaleString()}</li>}
                {personalLoanPayment > 0 && <li>Personal loans*: ${personalLoanPayment.toLocaleString()}</li>}
                {creditCardPayment > 0 && <li>Credit cards*: ${creditCardPayment.toLocaleString()}</li>}
                {otherDebt > 0 && <li>Other debt: ${otherDebt.toLocaleString()}</li>}
              </ul>
              {/* 2. Totals */}
              <div className="space-y-1">
                <p className="font-semibold">Total cost if you don’t file: ${costNotToFile.toLocaleString()}</p>
                <p className="font-semibold">Estimated savings by filing: ${totalSavings.toLocaleString()}</p>
              </div>
              {/* 3. Asterisk footnote */}
              <p className="text-xs text-gray-500">
                *Personal loan and credit card amounts assume a 25% APR amortized over 3 years
              </p>
            </div>
          ) : (
            <div className="text-gray-700">
              {slides[currentSlide].content}
            </div>
          )}
        </div>

        {/* Footer / Navigation */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-4">
          
          <button
            onClick={() => setCurrentSlide(i => Math.max(0, i - 1))}
            disabled={currentSlide === 0}
            className={`py-1 px-3 rounded ${currentSlide === 0 ? 'text-gray-400' : 'text-indigo-600 hover:underline'}`}
          >
            ← Back
          </button>

          <div className="text-sm text-gray-600">
            {currentSlide + 1} / {slides.length}
          </div>

          {currentSlide === slides.length - 1 ? (
            <>
              {(() => {
                const hasContactErrors =
                  !answers.firstName || !answers.lastName || !answers.email || !answers.phone ||
                  contactErrors.firstName || contactErrors.lastName || contactErrors.email || contactErrors.phone;
                return (
                  <button
                    onClick={onClose}
                    className={`bg-indigo-600 text-white py-1 px-4 rounded hover:bg-indigo-700 ${hasContactErrors ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Finish
                  </button>
                );
              })()}
            </>
          ) : (
            <button
              onClick={() => setCurrentSlide(i => Math.min(slides.length - 1, i + 1))}
              className="bg-indigo-600 text-white py-1 px-4 rounded hover:bg-indigo-700"
            >
              Next →
            </button>
          )}
          {/* At top of your modal JSX, inside the element with `className="relative"` */}
{(isSubmittingAttorney || showSubmissionConfirmation) && (
  <div className="absolute inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center z-50">
    {isSubmittingAttorney ? (
      // Spinner
      <svg
        className="animate-spin h-12 w-12 text-indigo-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v8H4z"
        />
      </svg>
    ) : (
      // Checkmark
      <CheckCircleIcon className="h-12 w-12 text-green-600 animate-pulse" />
    )}
    <p className="mt-4 text-lg font-medium text-gray-800 text-center px-4">
      {isSubmittingAttorney
        ? 'Submitting…'
        : 'Submitted to an attorney in your district. Someone will reach out to you soon.'}
    </p>
    <button
      type="button"
      onClick={() => setShowSubmissionConfirmation(false)}
      className="mt-4 bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 focus:outline-none"
    >
      Close
    </button>
  </div>
)}
        </div>
      </div>
    </div>
  );
}